import React, { useState } from 'react';
import styled from 'styled-components';
import { useData } from '../../contexts/DataContext';
import ElevatedButton from '../Layout/ElevatedButton';

const Form = styled.form`
  max-width: 400px;
  margin: 2rem auto;
  padding: 2rem;
  border: 1px solid ${props => props.theme.buttonPrimary};
  border-radius: 8px;
  background: ${props => props.theme.cardBackground};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  color: ${props => props.theme.text};
`;

const Input = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem;
  margin: 0.5rem 0;
  border: 1px solid ${props => props.theme.inputBorder};
  border-radius: 4px;
  font-size: 0.875rem;
  background: ${props => props.theme.inputBackground};
  color: ${props => props.theme.text};
  transition: all 0.2s ease;
  box-sizing: border-box;

  &::placeholder {
    color: ${props => props.theme.textMuted};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimary};
    box-shadow: 0 0 0 2px rgba(88, 101, 242, 0.1);
  }
`;

const HelpText = styled.div`
  font-size: 0.75rem;
  color: ${props => props.theme.textSecondary};
  margin: -0.25rem 0 0.5rem 0;
  line-height: 1.4;
  padding: 0.5rem;
  background: rgba(88, 101, 242, 0.1);
  border-radius: 4px;
  border: 1px solid ${props => props.theme.buttonPrimary};
`;

const HelpIcon = styled.span`
  display: inline-block;
  margin-right: 0.25rem;
  font-weight: bold;
  color: ${props => props.theme.buttonPrimary};
`;

const Select = styled.select`
  width: 100%;
  padding: 0.5rem 0.75rem;
  margin: 0.5rem 0;
  border: 1px solid ${props => props.theme.inputBorder};
  border-radius: 4px;
  font-size: 0.875rem;
  background: ${props => props.theme.inputBackground};
  color: ${props => props.theme.text};
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimary};
    box-shadow: 0 0 0 2px rgba(88, 101, 242, 0.1);
  }

  option {
    background: ${props => props.theme.inputBackground};
    color: ${props => props.theme.text};
  }
`;

const AssignmentForm = ({ assignment, onSubmit, onCancel, isEditing = false, courseId: propCourseId }) => {
  const { addAssignment, updateAssignment, courses, settings } = useData();
  const userTimezone = settings?.preferences?.timezone || 'UTC';
  
  const [title, setTitle] = useState(assignment?.title || '');
   const [dueDate, setDueDate] = useState(() => {
    if (assignment?.due_date) {
      try {
        // Parse the UTC date from the database
        const dateObj = new Date(assignment.due_date);
        if (!isNaN(dateObj.getTime())) {
          // Convert to user's timezone and format as YYYY-MM-DD
          return dateObj.toLocaleDateString('en-CA', { 
            timeZone: userTimezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          });
        }
      } catch (error) {
        console.warn('Error parsing assignment due date for date:', error);
      }
    }
    return '';
  });
  const [dueTime, setDueTime] = useState(() => {
    if (assignment?.due_date) {
      try {
        // Parse the UTC date from the database
        const dateObj = new Date(assignment.due_date);
        if (!isNaN(dateObj.getTime())) {
          // Convert to user's timezone and format as HH:MM
          return dateObj.toLocaleTimeString('en-US', { 
            timeZone: userTimezone,
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          });
        }
      } catch (error) {
        console.warn('Error parsing assignment due date for time:', error);
      }
    }
    // default to end of day
    return '23:59';
  });
  const [status, setStatus] = useState(assignment?.status || 'pending');
  const [weight, setWeight] = useState(assignment?.weight || 0);
  const [courseId, setCourseId] = useState(() => {
    if (assignment?.course) {
      // Handle both populated course objects and course ID strings
      return typeof assignment.course === 'object' && assignment.course?._id 
        ? assignment.course._id 
        : assignment.course;
    }
    // Use the prop courseId if provided (for adding assignments from course detail page)
    return propCourseId || '';
  });

  // Get the selected course color
  const selectedCourse = (courses || []).find(course => course._id === courseId);
  const courseColor = selectedCourse?.color || '#5865f2';

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate that a course is selected
    if (!courseId) {
      alert('Please select a course for this assignment.');
      return;
    }
    
    // Combine date and time in user's timezone, then convert to UTC
    let localDate = null;
    if (dueDate) {
      // Ensure time is in HH:MM format, default to 23:59 if not set
      let time = dueTime && dueTime.trim() !== '' ? dueTime : '23:59';
      
      // Ensure time has proper format (HH:MM)
      if (time.length === 5 && time.includes(':')) {
        // Time is already in correct format
      } else if (time.length === 4 && !time.includes(':')) {
        // Convert H:MM to HH:MM format
        time = time.padStart(5, '0');
      } else {
        // Fallback to default
        time = '23:59';
      }
      
      // Parse the date in the user's timezone and convert to UTC
      // Use Intl.DateTimeFormat to handle the conversion
      try {
        // Split date and time components
        const [year, month, day] = dueDate.split('-').map(Number);
        const [hours, minutes] = time.split(':').map(Number);
        
        // Create a date object representing this exact moment in the user's timezone
        // We need to find the UTC timestamp that corresponds to this local time
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: userTimezone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        });
        
        // Get the timezone offset for the user's timezone at this specific date/time
        // We'll use a different approach: format a known UTC time and calculate offset
        const testDate = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0));
        const parts = formatter.formatToParts(testDate);
        const tzYear = parseInt(parts.find(p => p.type === 'year').value);
        const tzMonth = parseInt(parts.find(p => p.type === 'month').value);
        const tzDay = parseInt(parts.find(p => p.type === 'day').value);
        const tzHour = parseInt(parts.find(p => p.type === 'hour').value);
        const tzMinute = parseInt(parts.find(p => p.type === 'minute').value);
        
        // Calculate the offset
        const utcTime = Date.UTC(year, month - 1, day, hours, minutes, 0);
        const tzTime = Date.UTC(tzYear, tzMonth - 1, tzDay, tzHour, tzMinute, 0);
        const offset = tzTime - testDate.getTime();
        
        // Apply reverse offset to get correct UTC time
        const correctUtcTime = utcTime - offset;
        const finalDate = new Date(correctUtcTime);
        
        // Verify the date is valid
        if (isNaN(finalDate.getTime())) {
          console.error('Invalid date/time combination:', dueDate, time);
          alert('Invalid date or time selected. Please check your input.');
          return;
        }
        
        // Store as ISO string (UTC)
        localDate = finalDate.toISOString();
      } catch (error) {
        console.error('Error converting timezone:', error);
        alert('Error processing date. Please try again.');
        return;
      }
    }

    const assignmentData = { 
      title, 
      due_date: localDate, 
      status, 
      course: courseId,
      color: courseColor,
      weight: parseFloat(weight) || 0
    };

    if (isEditing && assignment) {
      updateAssignment(assignment._id, assignmentData);
    } else {
      addAssignment(assignmentData);
    }

    // Reset form
    setTitle('');
    setDueDate('');
    setStatus('pending');
    setCourseId('');
    setWeight(0);

    if (onSubmit) onSubmit();
  };

  return (
    <Form onSubmit={handleSubmit}>
      <h2 style={{ color: 'var(--color-text)', textAlign: 'center' }}>
        {isEditing ? 'Edit Assignment' : 'Add Assignment'}
      </h2>
      <Input
        type="text"
        placeholder="Assignment Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        required
      />
      <Select value={courseId} onChange={(e) => setCourseId(e.target.value)} required>
        <option value="">Select Course *</option>
        {(courses || []).filter(course => course.status !== 'completed').map(course => (
          <option key={course._id} value={course._id}>
            {course.course_code} - {course.name}
          </option>
        ))}
      </Select>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <Input
          type="date"
          aria-label="Due date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          required
        />
        <Input
          type="time"
          aria-label="Due time"
          value={dueTime}
          onChange={(e) => setDueTime(e.target.value)}
        />
      </div>
      <Select value={status} onChange={(e) => setStatus(e.target.value)}>
        <option value="pending">Pending</option>
        <option value="in-progress">In Progress</option>
        <option value="completed">Completed</option>
      </Select>
      <Input
        type="number"
        placeholder="Weight Percentage (e.g., 7.5 for 7.5%)"
        value={weight}
        onChange={(e) => setWeight(e.target.value)}
        min="0"
        max="100"
        step="0.1"
      />
      <HelpText>
        <HelpIcon>How to calculate weight percentage:</HelpIcon>
        <br />
        <strong>Weight Percentage</strong> = The percentage this assignment contributes to your total course grade.<br/>
        <strong>Example:</strong> A 50-point quiz out of 1000 total course points = 5% weight<br/>
        <strong>Formula:</strong> (Assignment Points ÷ Total Course Points) × 100 = Weight %<br/>
        <br />
        <strong>Enter the percentage directly:</strong> For 7.5% of your course grade, enter "7.5"<br/>
        <strong>Important:</strong> All assignment weights in a course should sum to 100%. Check your syllabus!
      </HelpText>
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
        <ElevatedButton 
          type="button" 
          onClick={onCancel}
          style={{ flex: 1, background: '#6c757d', borderColor: '#6c757d' }}
        >
          Cancel
        </ElevatedButton>
        <ElevatedButton type="submit" style={{ flex: 1 }}>
          {isEditing ? 'Update Assignment' : 'Add Assignment'}
        </ElevatedButton>
      </div>
    </Form>
  );
};

export default AssignmentForm;
